import { Phone, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemo } from "react";
import { AttributionBadge } from "./AttributionBadge";

const categoryLabels = {
  full_payment: "Full Payment",
  installment: "Installment",
  flexible: "Flexible",
  no_contact: "No Contact",
};

const categoryColors = {
  full_payment: "bg-emerald-100 text-emerald-700",
  installment: "bg-purple-100 text-purple-700",
  flexible: "bg-yellow-100 text-yellow-700",
  no_contact: "bg-orange-100 text-orange-700",
};

function PaymentEvent({ payment, isLast }) {
  return (
    <div
      className={cn(
        "relative pl-4 pb-4 border-l border-border/40",
        isLast && "border-l-transparent",
      )}
    >
      <div className="absolute left-[-4px] top-1 w-2 h-2 rounded-full bg-emerald-500" />
      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              Payment Received
            </span>
            <AttributionBadge />
          </div>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {new Date(payment.createdAt).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        </div>
        <p className="text-xs font-semibold text-emerald-600">
          +₹{payment.amount?.toLocaleString()}
        </p>
        <p className="text-[10px] text-muted-foreground font-mono">
          #{payment.paymentId}
        </p>
      </div>
    </div>
  );
}

function MissedInstalmentEvent({ instalment, isLast }) {
  return (
    <div
      className={cn(
        "relative pl-4 pb-4 border-l border-red-200",
        isLast && "border-l-transparent",
      )}
    >
      <div className="absolute left-[-4px] top-1 w-2 h-2 rounded-full bg-red-400" />
      <div className="bg-red-50 border border-red-100 rounded-lg p-2.5 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
            Instalment {instalment.instalmentNumber} Missed
          </span>
          <span className="text-[10px] text-muted-foreground shrink-0">
            {new Date(instalment.expectedDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
        </div>
        {instalment.expectedAmount && (
          <p className="text-xs font-semibold tabular-nums text-red-600">
            ₹{instalment.expectedAmount?.toLocaleString()} expected — not
            received
          </p>
        )}
      </div>
    </div>
  );
}

export function CallHistoryTimeline({
  logs = [],
  payments = [],
  instalmentPlan,
  isLoading,
  onDeleteLog,
}) {
  const timelineItems = useMemo(() => {
    const logItems = logs.map((l) => ({
      type: "log",
      date: new Date(l.createdAt),
      data: l,
    }));
    const paymentItems = payments
      .filter((p) => p.isFollowUpAttributed)
      .map((p) => ({
        type: "payment",
        date: new Date(p.createdAt),
        data: p,
      }));
    const missedItems = (instalmentPlan?.instalments || [])
      .filter((i) => i.status === "missed")
      .map((i) => ({
        type: "missed_instalment",
        date: new Date(i.expectedDate),
        data: i,
      }));
    return [...logItems, ...paymentItems, ...missedItems].sort(
      (a, b) => b.date - a.date,
    );
  }, [logs, payments, instalmentPlan]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-2 w-2 rounded-full mt-1.5 shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Phone className="h-8 w-8 text-muted-foreground/30 mb-2" />
        <p className="text-sm text-muted-foreground">No call history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {timelineItems.map((item, idx) =>
        item.type === "payment" ? (
          <PaymentEvent
            key={`payment-${item.data._id}`}
            payment={item.data}
            isLast={idx === timelineItems.length - 1}
          />
        ) : item.type === "missed_instalment" ? (
          <MissedInstalmentEvent
            key={`missed-${item.data.instalmentNumber}`}
            instalment={item.data}
            isLast={idx === timelineItems.length - 1}
          />
        ) : (
          <div
            key={item.data._id}
            className={cn(
              "relative pl-4 pb-4 border-l border-border/40",
              idx === timelineItems.length - 1 && "border-l-transparent",
            )}
          >
            <div className="absolute left-[-4px] top-1 w-2 h-2 rounded-full bg-primary" />
            <div className="bg-muted/30 rounded-lg p-2.5 space-y-1">
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
                    categoryColors[item.data.dispositionCategory] ||
                      "bg-gray-100 text-gray-600",
                  )}
                >
                  {categoryLabels[item.data.dispositionCategory] ||
                    item.data.dispositionCategory}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(item.data.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              {item.data.remarks && (
                <p className="text-xs text-muted-foreground">
                  {item.data.remarks}
                </p>
              )}
              {item.data.nextActionScheduledFor && (
                <p className="text-[10px] text-muted-foreground">
                  Next action:{" "}
                  <span className="font-medium text-foreground">
                    {new Date(
                      item.data.nextActionScheduledFor,
                    ).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </p>
              )}
              {item.data.caller && (
                <p className="text-[10px] text-muted-foreground">
                  Logged by: {item.data.caller.name || item.data.caller.email}
                </p>
              )}
              {/* Delete button */}
              {onDeleteLog && (
                <div className="pt-1">
                  <button
                    onClick={() => onDeleteLog(item.data)}
                    className="flex items-center gap-1 text-[9px] text-muted-foreground hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete log
                  </button>
                </div>
              )}
            </div>
          </div>
        ),
      )}
    </div>
  );
}

/** Compact inline version for table expansion — shows last N logs */
export function InlineCallHistory({ logs = [], max = 3 }) {
  const shown = logs.slice(0, max);
  if (shown.length === 0) return null;

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border/50">
          <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[25%]">
            Date
          </th>
          <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[15%]">
            Disposition
          </th>
          <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[30%]">
            Remarks
          </th>
          <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[20%]">
            Next Action
          </th>
          <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[10%]">
            By
          </th>
        </tr>
      </thead>
      <tbody>
        {shown.map((log) => (
          <tr
            key={log._id}
            className="border-b border-border/30 last:border-b-0 hover:bg-muted/50"
          >
            <td className="px-4 py-1.5 text-muted-foreground">
              {new Date(log.createdAt).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </td>
            <td className="px-4 py-1.5">
              <span
                className={cn(
                  "text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
                  categoryColors[log.dispositionCategory] ||
                    "bg-gray-100 text-gray-600",
                )}
              >
                {categoryLabels[log.dispositionCategory] ||
                  log.dispositionCategory}
              </span>
            </td>
            <td className="px-4 py-1.5 text-muted-foreground truncate max-w-[200px]">
              {log.remarks || "—"}
            </td>
            <td className="px-4 py-1.5 text-muted-foreground">
              {log.nextActionScheduledFor
                ? new Date(log.nextActionScheduledFor).toLocaleDateString(
                    "en-IN",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    },
                  )
                : "—"}
            </td>
            <td className="px-4 py-1.5 text-muted-foreground truncate">
              {log.caller?.name || log.caller?.email || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
